// frontend/src/api/agents.js
/**
 * Agent chat API — thin wrapper around POST /api/agents/chat (P15-G5).
 */
import client from "./client";

/**
 * Send one turn to the agent assistant.
 *
 * A turn is either a typed message or a verdict on a pending action (P16-G0d):
 * Confirm/Cancel on an `action_pending` card sends `confirmAction` (and, on
 * confirm, the `pendingAction` to execute) with no message text.
 *
 * @param {object} params
 * @param {string|null} [params.message]   The user's message text, or null on a
 *   confirm/cancel turn.
 * @param {string|null} [params.sessionId] Existing session to continue, or null
 *   to start a new one.
 * @param {string|null} [params.agent] Force a persona ("aria"|"dev"|"sam"), or
 *   null to let the backend auto-route. Ignored by the backend once a session
 *   exists (the session's agent wins).
 * @param {boolean|null} [params.confirmAction] true to execute / false to drop a
 *   previously proposed above-limit action; null for an ordinary turn.
 * @param {object|null} [params.pendingAction] The action to execute, echoed back
 *   from the action_pending card on confirm.
 * @returns {Promise<{session_id: string, agent: string, response: string, conversation_turn: number, response_type: "message"|"action_pending", pending_action: object|null}>}
 */
export async function sendChatMessage({
  message = null,
  sessionId = null,
  agent = null,
  confirmAction = null,
  pendingAction = null,
}) {
  const response = await client.post("/api/agents/chat", {
    message,
    session_id: sessionId,
    agent,
    confirm_action: confirmAction,
    pending_action: pendingAction,
  });
  return response.data;
}

/**
 * End a chat session and persist it as a case (P18-G2).
 *
 * @param {string} sessionId The session to close.
 * @param {string|null} [resolutionType] "resolved"|"escalated"|"abandoned".
 * @returns {Promise<{case_id: string}>}
 */
export async function endSession(sessionId, resolutionType = null) {
  const response = await client.post("/api/agents/end-session", {
    session_id: sessionId,
    resolution_type: resolutionType,
  });
  return response.data;
}

/**
 * Submit a CSAT score (1–5) for a finished session's case (P18-G2).
 *
 * @param {string} caseId The case id returned by {@link endSession}.
 * @param {number} score Rating from 1 to 5.
 * @returns {Promise<{status: string}>}
 */
export async function submitCsat(caseId, score) {
  const response = await client.post("/api/agents/csat", {
    case_id: caseId,
    score,
  });
  return response.data;
}
